<template>
  <div class="dashboard">
    <!-- Header Card -->
    <div class="header-card">
      <div class="header-left">
        <img class="header-avatar" :src="currentUser.profile_picture || 'static/img/default-dp.png'" alt="avatar"
          @click="$router.replace(`/user/${currentUser.uid}`)">
        <div class="employee-info">
          <h1 class="emp-name">{{ currentUser.username }}</h1>
          <p class="emp-role">{{ currentUser.designation || 'Admin' }}</p>
          <div class="badges">
            <span v-if="joiningDateText" class="badge">{{ joiningDateText }}</span>
            <span class="badge">{{ todayText }}</span>
          </div>
        </div>
      </div>
      <div class="header-right">
        <span class="status-btn">Overview</span>
        <div class="checkin-time">
          <template v-if="loading.header">
            <span class="time">--</span>
            <span class="time-label">Counting active employees</span>
          </template>
          <template v-else>
            <span class="time">{{ presentToday }} / {{ totalActiveEmployees }}</span>
            <span class="time-label">Employees present today</span>
          </template>
        </div>
      </div>
    </div>

    <!-- Stats Row -->
    <div class="stats-row">
      <div class="stat-card" v-for="stat in stats" :key="stat.label">
        <template v-if="loading.stats">
          <div class="sk sk-label"></div>
          <div class="sk sk-value"></div>
          <div class="sk sk-sub"></div>
        </template>
        <template v-else>
          <p class="stat-label">{{ stat.label }}</p>
          <p class="stat-value" :style="{ color: stat.color }">{{ stat.value }}</p>
          <p class="stat-sub" :style="stat.subColor ? { color: stat.subColor } : null">{{ stat.sub }}</p>
        </template>
      </div>
    </div>

    <!-- Attendance + Leave Requests -->
    <div class="panels-row panels-row-triple">
      <!-- Attendance -->
      <div class="panel panel-attendance">
        <div class="panel-header">
          <div>
            <p class="panel-title">Attendance</p>
            <p class="panel-subtitle">THIS WEEK PERFORMANCE</p>
          </div>
          <router-link to="/reports" class="panel-link">View Report →</router-link>
        </div>

        <div class="att-summary">
          <span class="att-summary-value">{{ weekAttendancePct }}%</span>
          <span class="att-summary-label">
            average attendance · {{ weekRangeLabel }}
          </span>
        </div>

        <div class="att-bars" v-if="attendance.length">
          <div class="att-group" v-for="d in attendance" :key="d.key"
            :class="{ 'att-group--today': d.isToday, 'att-group--future': d.isFuture }" tabindex="0">
            <div class="att-pair">
              <div class="att-column">
                <!-- Hovering either bar reveals both figures for that day. -->
                <div class="att-tooltip" role="tooltip">
                  <p class="att-tooltip-title">{{ d.day }}, {{ d.date }}</p>
                  <p class="att-tooltip-row">
                    <span class="legend-dot legend-present"></span>
                    <span>{{ d.present }} present</span>
                  </p>
                  <p class="att-tooltip-row">
                    <span class="legend-dot legend-absent"></span>
                    <span>{{ d.absent }} absent</span>
                  </p>
                  <p class="att-tooltip-total" v-if="totalActiveEmployees">
                    {{ d.present }} of {{ totalActiveEmployees }} active
                  </p>
                </div>

                <div class="att-bar att-present" :style="{ height: attendanceBarHeight(d.present) + 'px' }"></div>
                <div class="att-bar att-absent" :style="{ height: attendanceBarHeight(d.absent) + 'px' }"></div>
              </div>
            </div>
            <span class="att-label">{{ d.day }}</span>
          </div>
        </div>

        <div class="att-skeleton" v-else-if="loading.attendance">
          <div class="sk sk-att" v-for="n in 7" :key="'sk-att-' + n"></div>
        </div>

        <div class="att-legend">
          <div class="legend-item"><span class="legend-dot legend-present"></span>Present</div>
          <div class="legend-item"><span class="legend-dot legend-absent"></span>Absent</div>
        </div>
      </div>

      <div class="panel panel-events">
        <div class="panel-header">
          <div>
            <p class="panel-title">Upcoming</p>
            <p class="panel-subtitle">COMPANY-WIDE MEETINGS &amp; HOLIDAYS</p>
          </div>
        </div>
        <div class="event-list">
          <template v-if="loading.events">
            <div class="event-row" v-for="n in 3" :key="'sk-evt-' + n">
              <div class="event-date">
                <div class="sk sk-day"></div>
                <div class="sk sk-mon"></div>
              </div>
              <div class="event-details">
                <div class="sk sk-title"></div>
                <div class="sk sk-desc"></div>
                <div class="sk sk-tag"></div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="event-row" v-for="(ev, i) in events" :key="i">
              <div class="event-date">
                <span class="event-day">{{ ev.day }}</span>
                <span class="event-month">{{ ev.month }}</span>
              </div>
              <div class="event-details">
                <p class="event-title">{{ ev.title }}</p>
                <p class="event-desc">{{ ev.desc }}</p>
                <span class="event-tag" :class="ev.tagClass">{{ ev.tag }}</span>
              </div>
            </div>
            <div v-if="!events.length" class="empty-state">
              <i class="now-ui-icons objects_globe" aria-hidden="true"></i>
              <p class="empty-state-title">Nothing scheduled ahead</p>
              <p class="empty-text">No upcoming meetings or holidays</p>
            </div>
          </template>
        </div>
      </div>

      <!-- Leave Requests -->
      <div class="panel panel-leaves">
        <div class="panel-header">
          <div>
            <p class="panel-title">Leave Requests</p>
            <p class="panel-subtitle">PENDING APPROVALS · LAST 30 DAYS</p>
          </div>
          <router-link to="/leave" class="panel-link">View All →</router-link>
        </div>
        <div class="leave-list">
          <template v-if="leavesLoading">
            <div class="leave-item" v-for="n in 4" :key="'sk-leave-' + n">
              <div class="leave-row">
                <div class="sk sk-circle"></div>
                <div class="sk-grow">
                  <div class="sk sk-title"></div>
                  <div class="sk sk-desc"></div>
                </div>
                <div class="leave-actions">
                  <div class="sk sk-btn"></div>
                  <div class="sk sk-btn"></div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="leave-item" v-for="(leave, i) in leaveRequests" :key="leave.id">
              <div class="leave-row">
                <div class="leave-avatar" :style="avatarStyle(i)">{{ initials(leave.name) }}</div>
                <div class="leave-info">
                  <p class="leave-name">{{ leave.name }}</p>
                  <p class="leave-date">{{ leave.dates }} · {{ leave.type }}</p>
                </div>
                <div class="leave-actions">
                  <button class="leave-btn btn-approve" :disabled="leave.busy"
                    @click="openDecision(leave, 'Approved')">Approve</button>
                  <button class="leave-btn btn-reject" :disabled="leave.busy"
                    @click="openDecision(leave, 'Rejected')">Reject</button>
                </div>
              </div>

              <div class="leave-decision" v-if="pendingDecision && pendingDecision.id === leave.id">
                <label class="decision-label" :for="'leave-comment-' + leave.id">
                  Comment
                  <span class="decision-hint">{{ pendingDecision.status === "Approved" ? "Approving" : "Rejecting" }} {{
                    leave.name }}'s request</span>
                </label>
                <textarea :id="'leave-comment-' + leave.id" class="decision-textarea" rows="2"
                  v-model="pendingDecision.comment" :placeholder="pendingDecision.status === 'Approved'
                    ? 'Reason for approving (optional)'
                    : 'Reason for rejecting (optional)'"></textarea>
                <div class="decision-actions">
                  <button class="leave-btn btn-cancel" :disabled="leave.busy" @click="cancelDecision">
                    Cancel
                  </button>
                  <button class="leave-btn"
                    :class="pendingDecision.status === 'Approved' ? 'btn-approve' : 'btn-reject'" :disabled="leave.busy"
                    @click="confirmDecision">
                    {{ leave.busy ? "Saving…" : "Confirm " + pendingDecision.status }}
                  </button>
                </div>
              </div>
            </div>
            <p v-if="!leaveRequests.length" class="empty-text">
              No pending leave requests from the last 30 days
            </p>
            <p v-if="stalePendingLeaves" class="stale-note">
              {{ stalePendingLeaves }} older pending
              {{ stalePendingLeaves === 1 ? "request" : "requests" }} in Leave Management
            </p>
          </template>
        </div>
      </div>
    </div>

    <!-- Payroll + Activity + Bugs -->
    <div class="panels-row panels-row-triple">
      <!-- Payroll -->
      <!-- <template v-if="loading.payroll">
        <div class="summary-row" v-for="n in 5" :key="'sk-pay-' + n">
          <div class="sk sk-key"></div>
          <div class="sk sk-val"></div>
        </div>
      </template> -->
      <template>
        <div class="panel panel-payroll">
          <div class="panel-header">
            <div>
              <p class="panel-title">Payroll — {{ payroll.monthLabel }}</p>
              <p class="panel-subtitle">SUMMARY</p>
            </div>
            <router-link to="/payroll" class="panel-link">View All →</router-link>
          </div>
          <div class="summary-row">
            <span class="summary-key">Total employees</span>
            <span class="summary-val">{{ payroll.payrollEmployeeCount }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-key">Processed</span>
            <span class="summary-val val-green">{{ payroll.processed }}</span>
          </div>
          <div class="summary-row" v-if="payroll.draft">
            <span class="summary-key">Draft</span>
            <span class="summary-val val-amber">{{ payroll.draft }}</span>
          </div>
          <div class="summary-row" v-if="payroll.hasEstimate">
            <span class="summary-key">Estimated from salary</span>
            <span class="summary-val val-amber">{{ payroll.estimatedCount }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-key">Total salaries</span>
            <span class="summary-val">{{ formatCurrency(payroll.realSalaries) }}</span>
          </div>
          <div class="summary-row" v-if="payroll.hasEstimate">
            <span class="summary-key">
              Pending salaries
              <span class="estimate-tag">ESTIMATED</span>
            </span>
            <span class="summary-val val-amber">{{ formatCurrency(payroll.estimatedSalaries) }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-key">Total taxes</span>
            <span class="summary-val">{{ formatCurrency(payroll.totalTaxes) }}</span>
          </div>
          <button class="primary-btn" @click="$router.push('/payroll')">Generate Payslips</button>
        </div>
      </template>

      <!-- Recent Activity -->
      <div class="panel panel-activity">
        <div class="panel-header">
          <div>
            <p class="panel-title">Recent Activity</p>
            <p class="panel-subtitle">{{ activitySubtitle }}</p>
          </div>
          <router-link to="/activity-logs" class="panel-link">View All →</router-link>
        </div>
        <div class="activity-list">
          <template v-if="loading.activity">
            <div class="activity-row" v-for="n in 5" :key="'sk-act-' + n">
              <div class="sk sk-dot"></div>
              <div class="sk-grow">
                <div class="sk sk-line"></div>
                <div class="sk sk-line-sm"></div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="activity-row" v-for="item in activity" :key="item.id">
              <span class="activity-dot" :style="{ backgroundColor: item.color }"></span>
              <div class="activity-body">
                <p class="activity-text">{{ item.text }}</p>
                <p class="activity-time">{{ item.time }}</p>
              </div>
            </div>
            <div v-if="!activity.length" class="empty-state">
              <i class="now-ui-icons ui-1_bell-53" aria-hidden="true"></i>
              <p class="empty-state-title">No activity yet</p>
              <p class="empty-text">Admin actions will show up here</p>
            </div>
          </template>
        </div>
      </div>

      <!-- Reported Bugs -->
      <div class="panel panel-bugs">
        <div class="panel-header">
          <div>
            <p class="panel-title">Reported Bugs</p>
            <p class="panel-subtitle">{{ bugsSubtitle }}</p>
          </div>
          <router-link to="/reported-bugs" class="panel-link">View All →</router-link>
        </div>
        <div class="bug-list">
          <template v-if="loading.bugs">
            <div class="bug-row" v-for="n in 4" :key="'sk-bug-' + n">
              <div class="sk-grow">
                <div class="sk sk-title"></div>
                <div class="sk sk-desc"></div>
              </div>
              <div class="sk sk-badge"></div>
            </div>
          </template>
          <template v-else>
            <div class="bug-row" v-for="bug in bugs" :key="bug.id">
              <div class="bug-body">
                <p class="bug-text">{{ bug.text }}</p>
                <p class="bug-meta">{{ bug.time }} · {{ bug.reporter }}</p>
              </div>
              <div class="bug-badges">
                <span class="bug-priority" :class="'bug-priority--' + bug.priorityClass" v-if="bug.priority">{{
                  bug.priority
                }}</span>
                <span class="bug-badge" :class="bug.resolved ? 'bug-resolved' : 'bug-open'">
                  {{ bug.resolved ? 'Resolved' : 'Open' }}
                </span>
              </div>
            </div>
            <div v-if="!bugs.length" class="empty-state">
              <i class="now-ui-icons objects_diamond" aria-hidden="true"></i>
              <p class="empty-state-title">Nothing to fix right now</p>
              <p class="empty-text">No bugs reported in the last 24 hours</p>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Upcoming Events + Employee Performance -->
    <div class="panels-row panels-row-triple">
      <!-- Employee Performance (last month) -->
      <div class="panel panel-performance">
        <div class="panel-header">
          <div>
            <p class="panel-title">Performance — {{ lastMonthRange.month }} {{ lastMonthRange.year }}</p>
            <p class="panel-subtitle">ALL EMPLOYEES</p>
          </div>
          <router-link to="/performance" class="panel-link">View All →</router-link>
        </div>
        <div class="perf-list">
          <template v-if="loading.performance">
            <div class="perf-row" v-for="n in 14" :key="'sk-perf-' + n">
              <div class="sk sk-emp"></div>
              <div class="sk sk-bar-lg"></div>
              <div class="sk sk-pct"></div>
              <div class="sk sk-badge"></div>
            </div>
          </template>
          <template v-else>
            <div class="perf-row" v-for="p in performance" :key="p.uid">
              <div class="perf-emp">
                <span class="perf-name">{{ p.name }}</span>
                <span class="perf-grade" v-if="p.gradeLabel">{{ p.gradeLabel }}</span>
              </div>
              <div class="perf-bar-track">
                <div class="perf-bar-fill" :style="{ width: p.pct + '%', backgroundColor: p.color }"></div>
              </div>
              <span class="perf-pct">{{ p.display }}</span>
              <span class="perf-badge" :class="p.badgeClass">{{ p.label }}</span>
            </div>
            <div v-if="!performance.length" class="empty-state">
              <i class="now-ui-icons business_chart-bar-32" aria-hidden="true"></i>
              <p class="empty-state-title">No performance data</p>
              <p class="empty-text">Nothing recorded for last month</p>
            </div>
          </template>
        </div>
      </div>

      <div class="panel panel-complains">
        <div class="panel-header">
          <div>
            <p class="panel-title">Complains</p>
            <p class="panel-subtitle">{{ complainsSubtitle }}</p>
          </div>
          <router-link to="/complains" class="panel-link">View All →</router-link>
        </div>
        <div class="complain-list">
          <template v-if="loading.complains">
            <div class="complain-row" v-for="n in 3" :key="'sk-cmp-' + n">
              <div class="sk sk-title"></div>
              <div class="sk sk-desc"></div>
              <div class="sk sk-tag"></div>
            </div>
          </template>
          <template v-else>
            <div class="complain-row" v-for="c in complains" :key="c.id">
              <div class="complain-body">
                <p class="complain-text">{{ c.text }}</p>
                <p class="complain-meta">
                  {{ c.time }}<span v-if="c.reporter"> · {{ c.reporter }}</span>
                </p>
              </div>
              <div class="complain-badges">
                <span class="complain-chip" :class="'complain-chip--' + c.priorityClass">{{ c.priority }}</span>
                <span class="complain-chip" :class="'complain-chip--' + c.statusClass">{{ c.status }}</span>
                <span class="complain-chip complain-chip--type" v-if="c.type !== 'General'">{{ c.type }}</span>
              </div>
            </div>
            <div v-if="!complains.length" class="empty-state">
              <i class="now-ui-icons ui-1_check" aria-hidden="true"></i>
              <p class="empty-state-title">Nothing to resolve</p>
              <p class="empty-text">No recent complains raised</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import firebase from "firebase/app";
import "firebase/database";
import firebaseDB from "../../../assets/config/firebase";
import { designationGrades } from "./UserProfile/ProfileData";
import swal from "sweetalert";
import { createActivityPayload, logAdminActivity } from "../../../utils/adminLogs";
import { handleActivityNotification } from "../../../services/notificationService";

export default {
  name: "AdminDashboard",
  data() {
    return {
      loading: {
        stats: true,
        header: true,
      },
      countedWithEmployees: false,
      busyLeaveIds: [],
      pendingDecision: null,

      presentToday: 0,
      newThisMonth: 0,

      payroll: {
        monthLabel: moment().format("MMMM YYYY"),
        processed: 0,
        draft: 0,
        awaiting: 0,
        estimatedCount: 0,
        // Active employees eligible for payroll (super admins excluded).
        payrollEmployeeCount: 0,
        totalSalaries: 0,
        totalTaxes: 0,
        // Real payroll figures only (Processed + Draft).
        realSalaries: 0,
        // Gross-salary estimate for employees with no payroll row yet.
        estimatedSalaries: 0,
        // True while any active employee is still represented by an estimate
        // rather than a real payroll record. Drives the "estimated" label.
        hasEstimate: true,
        // null means "not comparable" — the percentage is hidden until payroll
        // is processed for this month, per the PM's rule.
        changeVsLastMonth: null,
      },

      attendance: [],
      events: [],
      performance: [],
      complains: [],
      loading: {
        stats: true,
        header: true,
        attendance: true,
        events: true,
        performance: true,
        complains: true,
        payroll: true,
        activity: true,
        bugs: true,
      },

      activity: [],

      bugs: [],

      avatarPalette: [
        { background: "#e6f7f4", color: "#1a6040" },
        { background: "#eaf0f9", color: "#1a4a80" },
        { background: "#fff3cd", color: "#80400a" },
        { background: "#f0e8fb", color: "#4a1a80" },
      ],
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user || {};
    },
    leavesLoading() {
      return !Array.isArray(this.$store.state.auth.AllLeaves);
    },
    complainsSubtitle() {
      if (!this.complains.length) return "RECENT COMPLAINS";
      const count = this.complains.length;
      return `RECENT COMPLAINS · TOP ${count}`;
    },
    // Raw Trello cards from the complains store module. The module is not
    // namespaced, so its state sits directly under state.complains.
    rawComplains() {
      const mod = this.$store.state.complains;
      return mod && mod.cardsData;
    },
    // The Complains tab filters cards down to ACTIVE employees via
    // EmployeeDropdown (defaultFilter="active"), so the dashboard must apply
    // the same scope or the two screens show different rows.
    activeEmployeeEmails() {
      return new Set(this.activeEmployees.map(u => u.email).filter(Boolean));
    },
    // Last completed month only — the current month is still in progress.
    lastMonthRange() {
      const last = moment().subtract(1, "month");
      return {
        month: last.format("MMMM"),
        year: String(last.year()),
      };
    },
    companyUserIds() {
      const company = this.$store.state.auth.company;
      if (!company || !Array.isArray(company.users)) return null;
      return new Set(company.users.map(u => u.uid));
    },
    // Active employees of this company, mirroring the "Active Employees"
    // filter used by the User Reports screen (EmployeeStatusDropdown).
    activeEmployees() {
      const allUsers = this.$store.state.auth.allUsers;
      if (!Array.isArray(allUsers) || !allUsers.length) return [];

      const companyUserIds = this.companyUserIds;
      const scoped = companyUserIds
        ? allUsers.filter(u => companyUserIds.has(u.uid))
        : allUsers;

      return scoped.filter(u => u.isActive);
    },
    totalActiveEmployees() {
      return this.activeEmployees.length;
    },
    // Company-wide working days from Settings (constants.days). Empty means the
    // company hasn't configured any, in which case the full week is shown.
    workingDayNames() {
      const constants = this.$store.state.auth.constants;
      const days = constants && Array.isArray(constants.days) ? constants.days : [];
      return new Set(days.map(d => String(d).toLowerCase()));
    },
    weekRangeLabel() {
      if (!this.attendance.length) return "";
      const first = moment(this.attendance[0].key);
      const last = moment(this.attendance[this.attendance.length - 1].key);
      if (!first.isValid() || !last.isValid()) return "";
      return `${first.format("D MMM")} – ${last.format("D MMM YYYY")}`;
    },
    attendanceMax() {
      return this.attendance.reduce(
        (max, day) => Math.max(max, day.present, day.absent),
        0
      );
    },
    weekAttendancePct() {
      const total = this.totalActiveEmployees * this.attendance.length;
      if (!total) return 0;
      const present = this.attendance.reduce((sum, d) => sum + d.present, 0);
      return Math.round((present / total) * 100);
    },
    activitySubtitle() {
      return this.activity.length ? `LATEST ${this.activity.length}` : "LATEST ACTIVITY";
    },
    payrollSubLabel() {
      const change = this.payroll.changeVsLastMonth;
      // Hidden until there is a fair comparison to show.
      if (change === null || change === undefined) return "Payroll not processed yet";
      return `${change >= 0 ? "+" : ""}${change}% vs last month`;
    },
    bugsSubtitle() {
      return this.bugs.length ? `LATEST ${this.bugs.length}` : "LATEST REPORTS";
    },
    joiningDateText() {
      const joiningDate = this.currentUser.joiningDate;
      if (!joiningDate) return "";
      const date = moment(joiningDate);
      return date.isValid() ? `Joined ${date.format("D MMM YYYY")}` : "";
    },
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return "Good morning";
      if (hour < 18) return "Good afternoon";
      return "Good evening";
    },
    todayText() {
      return moment().format("ddd, D MMMM YYYY");
    },
    // Every pending leave belonging to this company, regardless of age.
    // The stat card is a backlog signal, so old requests still count.
    allPendingLeaves() {
      const leaves = this.$store.state.auth.AllLeaves;
      if (!Array.isArray(leaves)) return [];

      const companyUserIds = this.companyUserIds;
      return leaves.filter(leave => {
        if (!leave || leave.status !== "Pending") return false;
        if (!companyUserIds) return true;
        return companyUserIds.has(leave.userId);
      });
    },
    // The panel is an action surface, so it only surfaces recent requests.
    // Anything older is still counted above and handled in Leave Management.
    leaveRequests() {
      const cutoff = moment().subtract(30, "days");

      return this.allPendingLeaves
        .filter(leave => {
          const created = Number(leave.createdAt);
          if (!created) return false;
          return moment(created).isSameOrAfter(cutoff);
        })
        .sort((a, b) => b.createdAt - a.createdAt)
        .map(leave => ({
          id: leave.id,
          userId: leave.userId,
          name: leave.employee || this.findEmployeeName(leave.userId),
          dates: leave.dates || this.formatLeaveDates(leave),
          type: leave.type,
          busy: this.busyLeaveIds.includes(leave.id),
        }));
    },
    pendingLeaves() {
      return this.allPendingLeaves.length;
    },
    stalePendingLeaves() {
      return Math.max(0, this.pendingLeaves - this.leaveRequests.length);
    },
    absentToday() {
      return Math.max(0, this.totalActiveEmployees - this.presentToday);
    },
    stats() {
      const change = this.payroll.changeVsLastMonth;
      // Green when payroll grew, red when it shrank. Hidden entirely when
      // there is no comparable previous month.
      let payrollSubColor = null;
      if (change !== null && change !== undefined) {
        payrollSubColor = change < 0 ? "#e05c5c" : change > 0 ? "#28a06a" : "#8a9a98";
      }

      return [
        {
          label: "TOTAL EMPLOYEES",
          value: this.totalActiveEmployees,
          sub: `+${this.newThisMonth} this month`,
          color: "#1a1a1a",
        },
        {
          label: "PRESENT TODAY",
          value: this.presentToday,
          sub: `${this.absentToday} absent / on leave`,
          color: "#1a1a1a",
        },
        {
          label: "PENDING LEAVES",
          value: this.pendingLeaves,
          sub: this.stalePendingLeaves
            ? `${this.stalePendingLeaves} older than 30 days`
            : "awaiting approval",
          color: "#e0a800",
        },
        {
          label: `PAYROLL (${moment().format("MMM").toUpperCase()})${this.payroll.hasEstimate ? " · ESTIMATED" : ""}`,
          value: this.formatCompact(this.payroll.realSalaries),
          sub: this.payrollSubLabel,
          subColor: payrollSubColor,
          color: "#1a1a1a",
        },
      ];
    },
  },
  watch: {
    // App.vue fills allUsers asynchronously, so the first count can run against
    // an empty employee list. Re-run once the store actually has employees.
    activeEmployees(employees) {
      if (employees.length && !this.countedWithEmployees) {
        this.countedWithEmployees = true;
        this.fetchPresentToday();
        this.fetchWeekAttendance();
        this.fetchPayrollSummary();
        this.fetchUpcomingEvents();
        this.fetchPerformance();
        // Re-map complains too: the active-employee filter can only apply
        // once the roster has loaded.
        this.fetchRecentComplains();
      }
    },
    // Trello cards arrive after mount, so re-map the card whenever they land.
    rawComplains(cards) {
      if (!Array.isArray(cards) || !cards.length) return;
      this.complains = this.mapComplains(cards);
      this.loading.complains = false;
    },
  },
  mounted() {
    // Swap this for your real fetches. Each section can clear its own
    // loading flag, like EmployeeDashboard's loadSection does.
    this.fetchPresentToday();
    this.fetchNewThisMonth();
    this.fetchWeekAttendance();
    this.fetchRecentActivity();
    this.fetchRecentBugs();
    this.fetchPayrollSummary();
    this.fetchUpcomingEvents();
    this.fetchPerformance();
    this.fetchRecentComplains();
    this.loading.stats = false;
  },
  methods: {
    // Counts users created in the current month from the Activity Logs feed.
    // adminActivities.*.timestamp is written as toLocaleString('en-PK'),
    // i.e. "D/M/YYYY, h:mm:ss pm" — see createActivityPayload in utils/adminLogs.js.
    async fetchNewThisMonth() {
      try {
        const snapshot = await firebase.database().ref("adminActivities").once("value");
        const now = moment();
        const created = [];

        snapshot.forEach(child => {
          const activity = child.val() || {};
          if (activity.type !== "create_user") return;

          const date = this.parseActivityTimestamp(activity.timestamp);
          if (!date || !date.isValid()) return;
          if (date.isSame(now, "month") && date.isSame(now, "year")) {
            created.push(activity);
          }
        });

        this.newThisMonth = created.length;
      } catch (err) {
        console.error("Error fetching new employees this month:", err);
        this.newThisMonth = 0;
      }
    },

    // Accepts both the en-PK locale string and a parsable date/number, so the
    // count survives if the timestamp format ever changes on write.
    parseActivityTimestamp(timestamp) {
      if (!timestamp) return null;

      if (typeof timestamp === "number") return moment(timestamp);

      const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})\s*([ap]m)$/i.exec(timestamp);
      if (match) {
        const [, day, month, year, hours, minutes, seconds, meridiem] = match;
        let hour = Number(hours);
        if (/pm/i.test(meridiem) && hour !== 12) hour += 12;
        if (/am/i.test(meridiem) && hour === 12) hour = 0;
        return moment(
          new Date(Number(year), Number(month) - 1, Number(day), hour, Number(minutes), Number(seconds))
        );
      }

      return moment(timestamp);
    },

    // Counts distinct active employees who have a timeIn recorded today.
    // Mirrors the "Active Employees" filter of the User Reports screen.
    async fetchPresentToday() {
      try {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();

        const snapshot = await firebase
          .database()
          .ref("timePeriod")
          .orderByChild("timeIn")
          .startAt(startOfDay)
          .endAt(endOfDay)
          .once("value");

        const activeIds = this.activeEmployees
          .map(u => u.uid)
          .filter(Boolean);
        const activeIdSet = new Set(activeIds);

        const presentIds = new Set();
        snapshot.forEach(child => {
          const log = child.val() || {};
          if (activeIdSet.has(log.userId)) presentIds.add(log.userId);
        });

        this.presentToday = presentIds.size;
      } catch (err) {
        console.error("Error fetching present employees for today:", err);
        this.presentToday = 0;
      } finally {
        this.loading.header = false;
      }
    },

    // Reads one month's payroll and folds it into the active-employee roster.
    //
    // Per the PM's rules:
    //   (c) processed OR draft rows contribute their real finalAmount
    //   (b) active employees with no row yet contribute an ESTIMATE of their
    //       monthly salary from basicSalary + extraAllowance
    //   (a) only ACTIVE employees are counted — deactivated staff are excluded
    //       even when they still hold a processed row for that month
    //
    // Rows are de-duplicated by employeeId because processing a Pending row can
    // push a second record instead of updating in place.
    async fetchMonthPayroll(date) {
      const year = date.getFullYear();
      const month = moment(date).format("MMMM");
      const snapshot = await firebase
        .database()
        .ref(`payroll/${year}/${month}`)
        .once("value");

      // status precedence: Processed wins over Draft.
      const byEmployee = {};
      snapshot.forEach(child => {
        const record = child.val() || {};
        if (!record.employeeId) return;
        if (record.status !== "Processed" && record.status !== "Draft") return;

        const existing = byEmployee[record.employeeId];
        if (existing && existing.status === "Processed") return;

        byEmployee[record.employeeId] = {
          status: record.status,
          amount: Number(record.finalAmount) || 0,
          tax: Number(record.taxDeduction) || 0,
        };
      });

      // Super admins are excluded from payroll entirely — the Payroll Management
      // screen filters them out at PayrollTable.vue:203, so the dashboard must
      // not invent an estimated salary for them.
      const payrollEmployees = this.activeEmployees.filter(u => !u.isSuperAdmin);

      const totals = {
        salaries: 0,
        taxes: 0,
        processed: 0,
        draft: 0,
        estimated: 0,
        estimatedAmount: 0,
        processedAmount: 0,
        activeCount: payrollEmployees.length,
      };
      const byId = {};

      payrollEmployees.forEach(emp => {
        const row = byEmployee[emp.uid];

        if (row) {
          totals.salaries += row.amount;
          totals.taxes += row.tax;
          // Processed and Draft rows are real payroll figures.
          totals.processedAmount += row.amount;
          if (row.status === "Processed") totals.processed += 1;
          else totals.draft += 1;
          byId[emp.uid] = { amount: row.amount, estimated: false };
          return;
        }

        // No payroll row yet — estimate a full month from the fixed salary.
        // basicSalary/extraAllowance are monthly figures, matching how payroll
        // derives grossSalary in payrollHelper.js.
        const estimate =
          (Number(emp.basicSalary) || Number(emp.salary) || 0) +
          (Number(emp.extraAllowance) || 0);

        totals.salaries += estimate;
        totals.estimated += 1;
        totals.estimatedAmount += estimate;
        byId[emp.uid] = { amount: estimate, estimated: true };
      });

      return { year, month, byId, totals };
    },

    // Month-over-month comparison.
    //
    // PM's rule: an employee whose payroll is not processed (or is only Draft)
    // contributes their basic + extra allowance for BOTH months, so the
    // comparison always has figures on both sides and never reports
    // "not available" just because a month is unfinished.
    //
    // The headline "Total salaries" on the card still shows only real payroll
    // figures (rule c); estimated amounts are exposed separately so they can be
    // shown on their own line.
    async fetchPayrollSummary() {
      try {
        const now = moment();
        const prev = now.clone().subtract(1, "month");

        const [current, previous] = await Promise.all([
          this.fetchMonthPayroll(now.toDate()),
          this.fetchMonthPayroll(prev.toDate()),
        ]);

        this.payroll.monthLabel = `${current.month} ${current.year}`;
        this.payroll.payrollEmployeeCount = current.totals.activeCount;
        this.payroll.processed = current.totals.processed;
        this.payroll.draft = current.totals.draft;
        this.payroll.estimatedCount = current.totals.estimated;
        this.payroll.totalSalaries = current.totals.salaries;
        this.payroll.totalTaxes = current.totals.taxes;

        // (d) The estimated label disappears once every payroll-eligible
        // employee has a real Processed payroll for this month.
        const fullyProcessed =
          current.totals.processed > 0 &&
          current.totals.processed >= current.totals.activeCount;
        this.payroll.hasEstimate = !fullyProcessed;

        this.payroll.awaiting = Math.max(
          0,
          current.totals.activeCount - current.totals.processed
        );

        // Headline total shows real payroll only, with estimates itemised on
        // their own row (the user asked for a separate pending-salaries field).
        this.payroll.realSalaries = current.totals.processedAmount;
        this.payroll.estimatedSalaries = current.totals.estimatedAmount;

        const currentSum = current.totals.salaries;
        const previousSum = previous.totals.salaries;

        // Only a genuinely missing baseline (no previous payroll at all) can
        // block the comparison.
        if (!previousSum) {
          this.payroll.changeVsLastMonth = null;
          return;
        }

        const change = ((currentSum - previousSum) / previousSum) * 100;
        this.payroll.changeVsLastMonth =
          Math.abs(change) < 0.05 ? 0 : Number(change.toFixed(1));
      } catch (err) {
        console.error("Error fetching payroll summary:", err);
        this.payroll.changeVsLastMonth = null;
      } finally {
        this.loading.payroll = false;
      }
    },

    // Newest bug reports from the last 24 hours. bugReported timestamps are also
    // written as toLocaleString('en-PK') strings, so the shared parser applies.
    async fetchRecentBugs() {
      try {
        const list = await firebaseDB.getAllBugReports();

        const recent = [];
        (Array.isArray(list) ? list : []).forEach(bug => {
          if (!bug) return;
          const date = this.parseActivityTimestamp(bug.timestamp);
          if (!date || !date.isValid()) return;
          recent.push({ bug, date });
        });

        recent.sort((a, b) => b.date.valueOf() - a.date.valueOf());

        this.bugs = recent.slice(0, 4).map(({ bug, date }) => ({
          id: bug.id,
          text: bug.description || "No description provided",
          reporter: bug.email || "Unknown reporter",
          time: this.relativeTime(date),
          resolved: bug.status === "Resolved",
          priority: bug.priority || "",
          priorityClass: this.priorityClass(bug.priority),
        }));
      } catch (err) {
        console.error("Error fetching recent bugs:", err);
        this.bugs = [];
      } finally {
        this.loading.bugs = false;
      }
    },

    // BugReportModal offers Urgent / High / Normal / Low.
    priorityClass(priority) {
      const value = String(priority || "").toLowerCase();
      if (value === "urgent") return "urgent";
      if (value === "high") return "high";
      if (value === "low") return "low";
      if (value === "normal" || value === "medium") return "normal";
      return "unknown";
    },

    // Four most recent team complaints. Complains live on Trello and land in
    // the complains Vuex module asynchronously — fetchAllTrelloData does not
    // await its card fetch, so cardsData is usually still null at mount. A
    // one-shot read here showed a stale/empty list, so the card is driven by a
    // watcher on the store instead and re-renders whenever cards arrive.
    mapComplains(cards) {
      if (!Array.isArray(cards) || !cards.length) return [];

      // While allUsers is still loading the email set is empty; showing
      // nothing would be worse than briefly showing every row.
      const activeEmails = this.activeEmployeeEmails;

      return cards
        .filter(card => !activeEmails.size || activeEmails.has(this.complainEmail(card)))
        .slice()
        .sort((a, b) => new Date(b.dateLastActivity) - new Date(a.dateLastActivity))
        .slice(0, 6)
        .map(card => {
          const email = this.complainEmail(card);
          const name = typeof card.name === "string" ? card.name : "";
          const title = name.includes(" - ") ? name.split(" - ")[0] : name;

          // Feedback is appended to desc when submitted, so strip it back to
          // the original complaint text (mirrors AdminComplains.vue:325-328).
          const desc = card.desc || "";
          const text = desc.includes("Feedback Submitted")
            ? desc.split("Feedback Submitted")[0].trim()
            : desc.trim();

          return {
            id: card.id,
            text: title || text || "No description",
            reporter: email,
            time: this.relativeTime(moment(card.dateLastActivity)),
            priority: card.isHighPriority ? "High" : "Normal",
            priorityClass: card.isHighPriority ? "high" : "normal",
            status: card.status === "Resolved" ? "Resolved" : "Pending",
            statusClass: card.status === "Resolved" ? "resolved" : "pending",
            type: card.isPersonal ? "Personal" : "General",
          };
        });
    },

    // The reporter's email is the suffix of the Trello card name,
    // "title - reporter@company.com".
    complainEmail(card) {
      const name = typeof card.name === "string" ? card.name : "";
      if (!name.includes(" - ")) return "";
      return name.split(" - ")[1] || "";
    },

    async fetchRecentComplains() {
      try {
        this.complains = this.mapComplains(this.rawComplains);
      } catch (err) {
        console.error("Error fetching complains:", err);
        this.complains = [];
      } finally {
        this.loading.complains = false;
      }
    },

    // Performance for every active employee in the last completed month.
    // Mirrors EmployeeDashboard's scoring: raw score minus reopen and
    // deadline-missed penalties, expressed as a percentage of the target
    // defined for that employee's designation + grade.
    async fetchPerformance() {
      try {
        const { month, year } = this.lastMonthRange;

        // Read every card and bucket in memory, exactly like
        // EmployeeDashboard.getScoresByMonth. Filtering by month/year inside
        // fetchEditedCards compares with strict ===, so records storing year as
        // a number were silently dropped and produced wildly wrong scores.
        const [cards, criteria, targets] = await Promise.all([
          firebaseDB.fetchEditedCards({}),
          firebaseDB.getAllPerformanceCriteria(),
          firebaseDB.getAllPerformanceScoresByGrade(),
        ]);

        const ticketCrit = (criteria || []).find(c => c.type === "Ticket");
        const penalties = (ticketCrit && ticketCrit.rules && ticketCrit.rules.penalties) || {};
        const reopens = penalties.reopens || {};
        const deadlineMissed = penalties.deadlineMissed || {};

        // One read covers every employee, so group the cards by uid here.
        const byEmployee = {};
        Object.values(cards || {}).forEach(card => {
          if (!card || card.ticketStatus === "pending") return;
          if (!card.user) return;
          // Compare loosely on year so "2026" and 2026 both match.
          if (String(card.year) !== year) return;
          if (card.month !== month) return;

          const bucket = (byEmployee[card.user] =
            byEmployee[card.user] || { rawScore: 0, reopened: 0, deadlineMissedCount: 0, itemCount: 0 });

          const fields = this.resolvePerformanceFields(card);
          bucket.itemCount += 1;
          bucket.rawScore += fields.score;
          bucket.reopened +=
            fields.status !== "pending" && fields.status !== "rejected"
              ? fields.reopenCount
              : fields.originalreopenCount;
          bucket.deadlineMissedCount += fields.deadlineMissedCount;
        });

        // Target score lookup keyed on designation + grade.
        const targetMap = new Map(
          (targets || []).map(t => [`${t.designation}||${String(t.grade)}`, t])
        );

        this.performance = this.activeEmployees
          .map(emp => {
            const bucket = byEmployee[emp.uid];
            const target = targetMap.get(`${emp.designation}||${String(emp.grade)}`);
            const targetScore = target && Number(target.score) ? Number(target.score) : null;
            const gradeLabel = this.gradeLabelFor(emp);

            if (!bucket || !bucket.itemCount || !targetScore) {
              return {
                uid: emp.uid,
                name: emp.username || "Unknown",
                gradeLabel,
                pct: 0,
                display: "--",
                color: "#c3ccc9",
                label: "No data",
                badgeClass: "badge-none",
              };
            }

            const reopenDeduction =
              reopens.limit && reopens.score
                ? bucket.reopened >= reopens.limit ? reopens.score : 0
                : reopens.every && reopens.score
                  ? Math.floor(bucket.reopened / reopens.every) * reopens.score
                  : 0;

            const deadlineDeduction =
              deadlineMissed.limit && deadlineMissed.score
                ? bucket.deadlineMissedCount >= deadlineMissed.limit ? deadlineMissed.score : 0
                : deadlineMissed.every && deadlineMissed.score
                  ? Math.floor(bucket.deadlineMissedCount / deadlineMissed.every) * deadlineMissed.score
                  : 0;

            const netScore = bucket.rawScore + reopenDeduction + deadlineDeduction;
            const pct = Math.max(0, Math.round((netScore / targetScore) * 100));
            const rating = this.performanceRating(pct);

            return {
              uid: emp.uid,
              name: emp.username || "Unknown",
              gradeLabel,
              pct: Math.min(pct, 100),
              display: `${pct}%`,
              color: rating.color,
              label: rating.label,
              badgeClass: rating.badgeClass,
            };
          })
          .sort((a, b) => {
            // Highest performer first; "No data" rows sink to the bottom.
            if (a.display === "--" && b.display === "--") return 0;
            if (a.display === "--") return 1;
            if (b.display === "--") return -1;
            return b.pct - a.pct;
          });
      } catch (err) {
        console.error("Error fetching performance:", err);
        this.performance = [];
      } finally {
        this.loading.performance = false;
      }
    },

    // saveEditedCard writes only updatedData/originalData, never flat
    // score fields, so fall back through both shapes like the employee card.
    resolvePerformanceFields(card) {
      const original = card.originalData || {};
      const updated = card.updatedData || {};

      const pick = (value, ...fallbacks) => {
        if (value !== null && value !== undefined) return value;
        return fallbacks.find(v => v !== null && v !== undefined);
      };

      return {
        score: Number(pick(card.score, updated.score, original.score) || 0),
        reopenCount: Number(
          pick(card.reopenCount, updated.reopened, original.reopenCount) || 0
        ),
        originalreopenCount: Number(
          pick(card.originalreopenCount, original.reopenCount) || 0
        ),
        deadlineMissedCount: Number(
          pick(card.deadlineMissedCount, updated.deadlineMissedCount) || 0
        ),
        status: card.status || updated.status || "approved",
      };
    },

    performanceRating(pct) {
      if (pct >= 100) return { label: "Excellent", color: "#28a06a", badgeClass: "badge-excellent" };
      if (pct >= 80) return { label: "Good", color: "#2ec4a9", badgeClass: "badge-good" };
      if (pct >= 60) return { label: "Avg", color: "#5b85c3", badgeClass: "badge-avg" };
      if (pct >= 40) return { label: "Fair", color: "#e0a800", badgeClass: "badge-fair" };
      return { label: "Poor", color: "#e05c5c", badgeClass: "badge-poor" };
    },

    gradeLabelFor(emp) {
      if (!emp.grade) return "";
      const list = (designationGrades[emp.designation] || []);
      const match = list.find(g => String(g.value) === String(emp.grade));
      return match ? match.label : `Grade ${emp.grade}`;
    },

    // Builds the Upcoming panel from meetings and holidays across every
    // employee. Leave is deliberately excluded — it has its own card.
    async fetchUpcomingEvents() {
      try {
        const [meetingEvents, holidayEvents] = await Promise.all([
          this.buildMeetingEvents(),
          this.buildHolidayEvents(),
        ]);

        this.events = [...meetingEvents, ...holidayEvents]
          .sort((a, b) => a.sortDate - b.sortDate)
          .slice(0, 8);
      } catch (err) {
        console.error("Error fetching upcoming events:", err);
        this.events = [];
      } finally {
        this.loading.events = false;
      }
    },

    // Scheduled increment / quarterly meetings. Unlike the employee version
    // this reads every active employee's record from allUsers, which already
    // carries nextIncrementMeetingDetails and nextQuarterlyMeetingDetails.
    buildMeetingEvents() {
      const today = moment().startOf("day");

      return this.activeEmployees
        .flatMap(emp => {
          return [emp.nextIncrementMeetingDetails, emp.nextQuarterlyMeetingDetails]
            .filter(meeting => meeting && meeting.date)
            .map(meeting => {
              const date = this.parseEventDate(meeting.date);
              if (!date) return null;

              const isIncrement = meeting.type === "increment";
              let time = "";
              if (meeting.time) {
                const parsed = moment(meeting.time, "HH:mm");
                if (parsed.isValid()) time = parsed.format("h:mm A");
              }

              return {
                sortDate: date.valueOf(),
                day: date.format("DD"),
                month: date.format("MMM").toUpperCase(),
                title: meeting.title || (isIncrement
                  ? `${emp.username} · increment review`
                  : `${emp.username} · quarterly review`),
                desc: time ? `With manager · ${time}` : "With manager",
                tag: "Meeting",
                tagClass: "tag-meeting",
              };
            })
            .filter(Boolean);
        })
        .filter(item => item.sortDate >= today.valueOf());
    },

    // Meeting dates are written from a date picker, but older records may hold
    // a locale string that moment cannot parse. Fall back to the same
    // D/M/YYYY parsing used for activity logs so an event is never silently
    // dropped.
    parseEventDate(value) {
      if (!value) return null;

      const locale = this.parseActivityTimestamp(value);
      if (locale && locale.isValid()) return locale.startOf("day");

      const parsed = moment(value);
      return parsed.isValid() ? parsed.startOf("day") : null;
    },

    // Company holidays. Admins already see every shift's holidays, so no
    // per-user shift filtering here.
    async buildHolidayEvents() {
      const holidays = await firebaseDB.getAllHolidays();
      const today = moment().startOf("day");

      // Recurring holidays roll forward a whole year at a time; without a
      // horizon they would flood the panel with future occurrences.
      const horizon = moment(today).endOf("month").add(1, "month").endOf("month");

      return (Array.isArray(holidays) ? holidays : [])
        .map(h => {
          const base = moment(h.dates);
          if (!base.isValid()) return null;

          let start = base;
          if (h.isRecurring) {
            while (start.isSameOrBefore(today, "day")) {
              start = start.clone().add(1, "year");
            }
          }

          return { ...h, start };
        })
        .filter(item => item && item.start.isAfter(today, "day") && item.start.isSameOrBefore(horizon, "day"))
        .map(item => {
          const shift = (item.shift || "").toLowerCase();
          const scopeLabel =
            shift === "general"
              ? "Company-wide"
              : shift === "night"
                ? "Night shift"
                : "Morning shift";
          const days = item.numberOfDays === 1 ? "1 day" : `${item.numberOfDays} days`;

          return {
            sortDate: item.start.valueOf(),
            day: item.start.format("DD"),
            month: item.start.format("MMM").toUpperCase(),
            title: item.name,
            desc: `${scopeLabel} · ${days}`,
            tag: "Holiday",
            tagClass: "tag-holiday",
          };
        });
    },

    // Newest activity from the last 24 hours. Shares the timestamp parsing
    // above because adminActivities stores toLocaleString('en-PK') strings.
    async fetchRecentActivity() {
      try {
        const snapshot = await firebase.database().ref("adminActivities").once("value");

        const recent = [];
        snapshot.forEach(child => {
          const record = child.val() || {};
          const date = this.parseActivityTimestamp(record.timestamp);
          if (!date || !date.isValid()) return;
          recent.push({ id: child.key, record, date });
        });

        recent.sort((a, b) => b.date.valueOf() - a.date.valueOf());

        this.activity = recent.slice(0, 4).map(({ id, record, date }) => ({
          id,
          text: record.activityName || record.activityTitle || "Activity",
          time: this.relativeTime(date),
          color: this.activityColor(record.action),
        }));
      } catch (err) {
        console.error("Error fetching recent activity:", err);
        this.activity = [];
      } finally {
        this.loading.activity = false;
      }
    },

    relativeTime(date) {
      const minutes = moment().diff(date, "minutes");
      if (minutes < 1) return "Just now";
      if (minutes < 60) return `${minutes} min ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hr${hours > 1 ? "s" : ""} ago`;
      return date.format("D MMM, h:mm A");
    },

    activityColor(action) {
      const value = String(action || "").toLowerCase();
      if (value.includes("reject") || value.includes("delete") || value.includes("remove")) {
        return "#e05c5c";
      }
      if (value.includes("approve") || value.includes("add") || value.includes("creat")) {
        return "#28a06a";
      }
      if (value.includes("request") || value.includes("update") || value.includes("edit")) {
        return "#e0a800";
      }
      return "#2ec4a9";
    },

    // Per-day present/absent counts for the current week, restricted to the
    // company's configured working days so weekends don't read as absences.
    // An employee is counted present once per day regardless of how many
    // sessions they clocked. Absent = active headcount - present.
    async fetchWeekAttendance() {
      try {
        const today = moment();
        const start = today.clone().startOf("isoWeek");
        const end = start.clone().endOf("isoWeek");

        const weekDays = Array.from({ length: 7 }, (_, i) => start.clone().add(i, "days"));
        const workingSet = this.workingDayNames;
        const dayIndexes = weekDays
          .map((day, index) => ({ index, name: day.format("dddd").toLowerCase() }))
          .filter(({ name }) => !workingSet.size || workingSet.has(name));

        const shown = dayIndexes.length
          ? dayIndexes
          : weekDays.map((day, index) => ({ index, name: day.format("dddd").toLowerCase() }));

        const snapshot = await firebase
          .database()
          .ref("timePeriod")
          .orderByChild("timeIn")
          .startAt(start.valueOf())
          .endAt(end.valueOf())
          .once("value");

        const activeIdSet = new Set(
          this.activeEmployees.map(u => u.uid).filter(Boolean)
        );

        const perDay = {};
        shown.forEach(({ index }) => {
          perDay[index] = new Set();
        });

        snapshot.forEach(child => {
          const log = child.val() || {};
          if (!activeIdSet.has(log.userId)) return;

          const dayIndex = moment(log.timeIn).diff(start, "days");
          if (perDay[dayIndex]) {
            perDay[dayIndex].add(log.userId);
          }
        });

        const total = this.totalActiveEmployees;
        this.attendance = shown.map(({ index }) => {
          const day = weekDays[index];
          const present = perDay[index].size;
          return {
            key: day.format("YYYY-MM-DD"),
            day: day.format("ddd"),
            date: day.format("D MMM"),
            present,
            absent: Math.max(0, total - present),
            isToday: day.isSame(today, "day"),
            isFuture: day.isAfter(today, "day"),
          };
        });
      } catch (err) {
        console.error("Error fetching weekly attendance:", err);
        this.attendance = [];
      } finally {
        this.loading.attendance = false;
      }
    },

    // Scales against the week's peak day so the bars stay readable even when
    // the headcount is large or attendance is low.
    attendanceBarHeight(value) {
      if (!this.attendanceMax || !value) return 0;
      const max = 76;
      return Math.max(3, Math.round((value / this.attendanceMax) * max));
    },

    initials(name) {
      return (name || "")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join("");
    },

    avatarStyle(index) {
      return this.avatarPalette[index % this.avatarPalette.length];
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "PKR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(amount) || 0);
    },

    formatCompact(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "PKR",
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(Number(amount) || 0);
    },

    openDecision(leave, status) {
      if (!leave || leave.busy) return;
      this.pendingDecision = {
        id: leave.id,
        status,
        comment: "",
      };
    },

    cancelDecision() {
      this.pendingDecision = null;
    },

    async confirmDecision() {
      const decision = this.pendingDecision;
      if (!decision) return;

      const leave = this.leaveRequests.find(l => l.id === decision.id);
      if (!leave) {
        this.pendingDecision = null;
        return;
      }

      const comment = (decision.comment || "").trim();
      await this.updateLeave(leave, decision.status, comment);
      this.pendingDecision = null;
    },

    // Mirrors AdminLeaveManagement.submitAction so approving here keeps the
    // employee's leavesTaken balance in sync, and logs to Activity Logs.
    async updateLeave(leave, status, comment) {
      if (!leave || !leave.id || leave.busy) return;
      this.busyLeaveIds = [...this.busyLeaveIds, leave.id];

      const source = this.allPendingLeaves.find(l => l.id === leave.id);
      if (!source) {
        this.busyLeaveIds = this.busyLeaveIds.filter(id => id !== leave.id);
        return;
      }

      try {
        if (source.userId === this.currentUser.uid) {
          swal("Error", "You cannot approve or reject your own leave.", "error");
          return;
        }

        const employee = await firebaseDB.getSingleUser(source.userId);
        let leavesTaken = null;

        if (employee) {
          leavesTaken = Number(employee.leavesTaken) || 0;
          const days = Number(source.noofdays) || 0;
          if (source.status !== "Approved" && status === "Approved") {
            leavesTaken = (leavesTaken + days).toFixed(1);
          } else if (source.status === "Approved" && status === "Rejected") {
            leavesTaken = (leavesTaken - days).toFixed(1);
          }
        }

        await firebaseDB.updateLeaveStatusAndComment(
          leave.id,
          status,
          source.userId,
          comment,
          leavesTaken
        );

        await this.logLeaveDecision(source, status, employee, comment);
        swal(
          "Success",
          comment
            ? `Leave ${status.toLowerCase()}. Comment saved.`
            : `Leave ${status.toLowerCase()}.`,
          "success"
        );
      } catch (err) {
        console.error(`Error marking leave ${status.toLowerCase()}:`, err);
        swal("Error", `Could not ${status.toLowerCase()} the leave request.`, "error");
      } finally {
        this.busyLeaveIds = this.busyLeaveIds.filter(id => id !== leave.id);
      }
    },

    async logLeaveDecision(leave, status, employee, comment) {
      try {
        const name = leave.employee || (employee && employee.username) || "employee";
        const email = (employee && employee.email) || "";
        const start = leave.startDate || "";
        const end = leave.endDate || start;
        const dayText =
          leave.noofdays === 0.5 ? "half day" : leave.noofdays > 1 ? "days" : "day";
        const range =
          leave.noofdays > 1
            ? `from ${start} to ${end}`
            : `for ${start}`;

        const title = `${this.currentUser.username} ${status.toLowerCase()} ${name}'s ${leave.type} request ${range} (${leave.noofdays} ${dayText})`;
        const description = `${this.currentUser.email} ${status.toLowerCase()} ${email}'s ${leave.type} request from ${start} to ${end} (${leave.noofdays} ${dayText}).`;

        await logAdminActivity(
          createActivityPayload({
            user: this.currentUser,
            title,
            description,
            reason: comment || null,
            username: name,
            type: "leaves",
            uid: leave.userId
          })
        );

        await handleActivityNotification(
          {
            type: "leaves",
            activityDescription: description,
            activityName: title,
            activityOnUser: leave.userId,
            activityOnUserName: name,
            activityByUserName: this.currentUser.username,
          },
          this.$store.state.auth,
          this.currentUser.uid
        );
      } catch (err) {
        console.error("Failed to log leave activity:", err);
      }
    },

    findEmployeeName(uid) {
      const user = (this.$store.state.auth.allUsers || []).find(u => u.uid === uid);
      return (user && user.username) || "Unknown User";
    },

    formatLeaveDates(leave) {
      const start = moment(leave.startDate);
      const end = moment(leave.endDate || leave.startDate);
      if (!start.isValid()) return "-";
      if (!end.isValid() || end.isSame(start, "day")) return start.format("D MMM");
      return `${start.format("D MMM")} – ${end.format("D MMM")}`;
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.dashboard {
  min-height: 100vh;
  min-width: 0;
  max-width: 100%;
  padding: 24px;
  color: #1a1a1a;
}

/* ── Header ── */
.header-card {
  background: #3F4C49;
  border-radius: 12px;
  padding: 24px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
  color: #fff;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 18px;
  flex: 1 1 280px;
  min-width: 0;
}

.header-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  padding: 2px;
  border: 2px solid gray;
  cursor: pointer;
  object-fit: cover;
  flex-shrink: 0;
}

.employee-info {
  min-width: 0;
}

.emp-name {
  font-size: clamp(18px, 2.4vw, 22px);
  font-weight: 700;
  color: #fff;
  overflow-wrap: anywhere;
}

.emp-role {
  color: #a8bab8;
  font-size: 14px;
  margin: 3px 0 8px;
}

.badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  background: transparent;
  border: 1px solid #56C0A5;
  color: #a8bab8;
  border-radius: 4px;
  padding: 2px 10px;
  font-size: 12px;
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.checkin-time {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.status-btn {
  border: 1.5px solid #56C0A5;
  color: #56C0A5;
  border-radius: 6px;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 500;
}

.time {
  font-size: clamp(26px, 4vw, 32px);
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.time-label {
  font-size: 12px;
  color: #a8bab8;
  text-align: right;
}

/* ── Stats Row ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border-radius: 10px;
  padding: 20px 20px 16px;
  min-width: 0;
}

.stat-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: #8a9a98;
  margin-bottom: 6px;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}

.stat-value {
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 700;
  line-height: 1.1;
  overflow-wrap: anywhere;
}

.stat-sub {
  font-size: 12px;
  color: #8a9a98;
  margin: 2px 0 12px;
}

/* ── Skeletons ── */
.sk {
  border-radius: 6px;
  background: linear-gradient(90deg, #eef1f0 25%, #e2e7e6 50%, #eef1f0 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s infinite;
}

@keyframes sk-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {

  .sk,
  .sk-att {
    animation: none;
  }
}

.sk-label {
  width: 60%;
  height: 10px;
  margin-bottom: 14px;
}

.sk-value {
  width: 45%;
  height: 26px;
  margin-bottom: 8px;
}

.sk-sub {
  width: 70%;
  height: 10px;
  margin-bottom: 14px;
}

.sk-day {
  width: 26px;
  height: 16px;
}

.sk-mon {
  width: 26px;
  height: 8px;
  margin-top: 5px;
}

.sk-title {
  width: 65%;
  height: 12px;
  margin-bottom: 7px;
}

.sk-desc {
  width: 45%;
  height: 10px;
  margin-bottom: 8px;
}

.sk-tag {
  width: 58px;
  height: 16px;
  border-radius: 4px;
}

.sk-emp {
  width: 120px;
  max-width: 100%;
  height: 12px;
  flex-shrink: 0;
}

.sk-bar-lg {
  height: 8px;
  border-radius: 99px;
  flex: 1;
}

.sk-pct {
  width: 30px;
  height: 12px;
  flex-shrink: 0;
}

.sk-badge {
  width: 68px;
  height: 20px;
  border-radius: 4px;
  flex-shrink: 0;
}

.sk-grow {
  flex: 1;
  min-width: 0;
}

.sk-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sk-btn {
  width: 54px;
  height: 22px;
  border-radius: 4px;
}

.sk-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}

.sk-line {
  width: 85%;
  height: 11px;
  margin-bottom: 7px;
}

.sk-line-sm {
  width: 35%;
  height: 9px;
}

.sk-key {
  width: 90px;
  height: 11px;
}

.sk-val {
  width: 60px;
  height: 12px;
}

/* ── Panels / grid ──
   Desktop: 3 equal columns, Performance spans 2.
   minmax(0, 1fr) lets columns shrink below their content width, so long
   names, emails and tables can never push the page sideways. */
.panels-row,
.panels-row-triple {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.panel {
  background: #fff;
  border-radius: 10px;
  padding: 22px 22px 18px;
  min-width: 0;
}

.panel-performance {
  grid-column: span 2;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-bottom: 18px;
}

.panel-header>div {
  min-width: 0;
}

.panel-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  overflow-wrap: anywhere;
}

.panel-subtitle {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #8a9a98;
  margin-top: 2px;
}

.panel-link {
  font-size: 13px;
  color: #3D5851;
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
}

.empty-text {
  font-size: 13px;
  color: #8a9a98;
  margin: 4px 0 0;
}

.stale-note {
  font-size: 12px;
  color: #8a9a98;
  margin: 10px 0 0;
  padding-top: 10px;
  border-top: 1px solid #f0f2f1;
}

/* ── Attendance ── */
.att-summary {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.att-summary-value {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
}

.att-summary-label {
  font-size: 12px;
  color: #8a9a98;
}

.att-bars {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 104px;
}

.att-group {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  position: relative;
  outline: none;
}

.att-pair {
  display: flex;
  align-items: flex-end;
  height: 80px;
  width: 100%;
  justify-content: center;
}

.att-column {
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 100%;
  position: relative;
}

.att-bar {
  width: 12px;
  border-radius: 2px 2px 0 0;
  transition: height 0.4s ease, opacity 0.2s ease;
}

.att-present {
  background: #2ec4a9;
}

.att-absent {
  background: #dfe5e3;
}

.att-group:hover .att-bar,
.att-group:focus .att-bar {
  opacity: 1;
}

.att-bars:hover .att-group:not(:hover) .att-bar {
  opacity: 0.45;
}

.att-group--today .att-label {
  color: #3d5851;
  font-weight: 700;
  position: relative;
}

.att-group--today .att-label::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -6px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #56c0a5;
}

.att-group--future {
  opacity: 0.4;
}

/* Tooltip */
.att-tooltip {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  background: #1a1a1a;
  color: #fff;
  border-radius: 7px;
  padding: 9px 11px;
  min-width: 130px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s;
  z-index: 20;
}

.att-group:hover .att-tooltip,
.att-group:focus .att-tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.att-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #1a1a1a;
}

/* First and last day anchor the tooltip to their own edge so it never
   clips outside the card (matters most on narrow screens). */
.att-group:first-child .att-tooltip {
  left: 0;
  transform: translateY(4px);
}

.att-group:first-child:hover .att-tooltip,
.att-group:first-child:focus .att-tooltip {
  transform: translateY(0);
}

.att-group:first-child .att-tooltip::after {
  left: 14px;
  transform: none;
}

.att-group:last-child .att-tooltip {
  left: auto;
  right: 0;
  transform: translateY(4px);
}

.att-group:last-child:hover .att-tooltip,
.att-group:last-child:focus .att-tooltip {
  transform: translateY(0);
}

.att-group:last-child .att-tooltip::after {
  left: auto;
  right: 14px;
  transform: none;
}

.att-tooltip-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a8bab8;
  margin-bottom: 6px;
}

.att-tooltip-row {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  line-height: 1.7;
  white-space: nowrap;
}

.att-tooltip-total {
  font-size: 11px;
  color: #a8bab8;
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  white-space: nowrap;
}

.att-skeleton {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 104px;
}

.sk-att {
  flex: 1;
  height: 56px;
  border-radius: 4px 4px 0 0;
  background: linear-gradient(90deg, #eef1f0 25%, #e2e7e6 50%, #eef1f0 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s infinite;
}

.att-label {
  font-size: 11px;
  color: #8a9a98;
}

.att-legend {
  display: flex;
  gap: 16px;
  margin-top: 14px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.legend-present {
  background: #2ec4a9;
}

.legend-absent {
  background: #e8eceb;
}

/* ── Scrollable lists (scrollbar hidden) ── */
.event-list,
.leave-list {
  max-height: 240px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.event-list::-webkit-scrollbar,
.leave-list::-webkit-scrollbar {
  display: none;
}

.panel-events,
.panel-leaves {
  position: relative;
}

.panel-events::after,
.panel-leaves::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 28px;
  border-radius: 0 0 10px 10px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff);
  pointer-events: none;
}

/* ── Leave Requests ── */
.leave-item {
  border-bottom: 1px solid #f0f2f1;
}

.leave-item:last-child {
  border-bottom: none;
}

/* Wraps so Approve / Reject drop under the name when the card is narrow. */
.leave-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 10px 0;
}

.leave-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.leave-info {
  flex: 1 1 120px;
  min-width: 0;
}

.leave-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  overflow-wrap: anywhere;
}

.leave-date {
  font-size: 12px;
  color: #8a9a98;
  overflow-wrap: anywhere;
}

.leave-actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
}

.leave-decision {
  background: #f8faf9;
  border: 1px solid #e8eceb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
}

.decision-label {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 7px;
}

.decision-hint {
  font-size: 11px;
  font-weight: 500;
  color: #8a9a98;
  overflow-wrap: anywhere;
}

.decision-textarea {
  width: 100%;
  resize: vertical;
  padding: 8px 10px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.4;
  color: #1a1a1a;
  background: #fff;
  border: 1px solid #dfe4e3;
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.decision-textarea:focus {
  border-color: #56c0a5;
  box-shadow: 0 0 0 3px rgba(86, 192, 165, 0.18);
}

.decision-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.leave-btn {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid;
  cursor: pointer;
  transition: opacity 0.2s;
}

.leave-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-approve {
  background: #e4f4ee;
  color: #28a06a;
  border-color: #b7e0cd;
}

.btn-reject {
  background: #fdecec;
  color: #e05c5c;
  border-color: #f3c3c3;
}

.btn-cancel {
  background: #fff;
  color: #55635f;
  border-color: #dfe4e3;
}

.btn-cancel:hover {
  background: #f2f5f4;
}

/* ── Payroll ── */
.panel-payroll {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f2f1;
}

.summary-key {
  font-size: 13px;
  color: #555;
}

.estimate-tag {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.05em;
  background: #fff8e1;
  color: #c8920a;
  border: 1px solid #f0e0b0;
  border-radius: 3px;
  padding: 1px 5px;
  margin-left: 6px;
  vertical-align: middle;
}

.summary-val {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: right;
  overflow-wrap: anywhere;
}

.val-green {
  color: #28a06a;
}

.val-amber {
  color: #e0a800;
}

.primary-btn {
  width: 100%;
  margin-top: 14px;
  background: #3D5851;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  align-self: flex-end;
}

.primary-btn:hover {
  background: #56C0A5;
}

/* ── Activity ── */
.activity-row {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f2f1;
}

.activity-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.activity-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}

.activity-body {
  flex: 1;
  min-width: 0;
}

.activity-text {
  font-size: 13px;
  color: #1a1a1a;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.activity-time {
  font-size: 11px;
  color: #8a9a98;
  margin-top: 2px;
}

/* ── Events ── */
.event-row {
  display: flex;
  gap: 14px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f2f1;
}

.event-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.event-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 40px;
  flex-shrink: 0;
}

.event-day {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
}

.event-month {
  font-size: 10px;
  font-weight: 600;
  color: #8a9a98;
  letter-spacing: 0.05em;
}

.event-details {
  flex: 1;
  min-width: 0;
}

.event-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.event-desc {
  font-size: 12px;
  color: #8a9a98;
  margin: 2px 0 5px;
  overflow-wrap: anywhere;
}

.event-tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 10px;
}

.tag-meeting {
  background: #e4edff;
  color: #3b6dd4;
}

.tag-holiday {
  background: #e6f7f4;
  color: #2ec4a9;
}

/* ── Performance ── */
/* Flexible row: the bar absorbs spare width and the name shrinks. */
.perf-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #f0f2f1;
}

.perf-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.perf-emp {
  flex: 0 1 148px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.perf-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.perf-grade {
  font-size: 10px;
  color: #8a9a98;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.perf-bar-track {
  flex: 1 1 60px;
  min-width: 40px;
  height: 8px;
  background: #e8eceb;
  border-radius: 99px;
  overflow: hidden;
}

.perf-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.4s ease;
}

.perf-pct {
  font-size: 13px;
  color: #555;
  width: 38px;
  text-align: right;
  flex-shrink: 0;
}

.perf-badge {
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 8px;
  width: 68px;
  text-align: center;
  flex-shrink: 0;
}

.badge-excellent {
  background: #e4f4ee;
  color: #28a06a;
}

.badge-good {
  background: #e6f7f4;
  color: #2ec4a9;
}

.badge-avg {
  background: #eaf0f9;
  color: #5b85c3;
}

.badge-fair {
  background: #fff8e1;
  color: #c8920a;
}

.badge-poor {
  background: #fdecec;
  color: #e05c5c;
}

.badge-none {
  background: #f1f4f3;
  color: #98a5a2;
}

/* ── Complains ── */
/* Chips wrap under the text when the card is narrow (e.g. one-third width). */
.complain-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  background: #f8faf9;
  border: 1px solid #e8eceb;
  border-radius: 6px;
  margin-bottom: 8px;
}

.complain-row:last-child {
  margin-bottom: 0;
}

.complain-body {
  flex: 1 1 160px;
  min-width: 0;
}

.complain-badges {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  flex: 0 1 auto;
}

.complain-chip {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 4px;
  padding: 2px 8px;
  white-space: nowrap;
}

.complain-chip--high {
  background: #fdecec;
  color: #c33;
}

.complain-chip--normal {
  background: #eaf0f9;
  color: #1a4a80;
}

.complain-chip--pending {
  background: #fff8e1;
  color: #c8920a;
}

.complain-chip--resolved {
  background: #e4f4ee;
  color: #28a06a;
}

.complain-chip--type {
  background: #eef2f1;
  color: #55635f;
}

.complain-text {
  font-size: 13px;
  color: #1a1a1a;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.complain-meta {
  font-size: 11px;
  color: #8a9a98;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

/* ── Bugs ── */
.bug-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bug-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  background: #f8faf9;
  border: 1px solid #e8eceb;
  border-radius: 6px;
}

.bug-body {
  flex: 1 1 140px;
  min-width: 0;
}

.bug-text {
  font-size: 13px;
  color: #1a1a1a;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.bug-meta {
  font-size: 11px;
  color: #8a9a98;
  margin-top: 3px;
  overflow-wrap: anywhere;
}

.bug-badges {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  flex: 0 1 auto;
}

.bug-badge {
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 8px;
  white-space: nowrap;
}

.bug-open {
  background: #fff8e1;
  color: #c8920a;
}

.bug-resolved {
  background: #e4f4ee;
  color: #28a06a;
}

.bug-priority {
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  padding: 2px 8px;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.bug-priority--urgent {
  background: #fdecec;
  color: #c33;
}

.bug-priority--high {
  background: #fff1e0;
  color: #c2620a;
}

.bug-priority--normal {
  background: #eaf0f9;
  color: #1a4a80;
}

.bug-priority--low,
.bug-priority--unknown {
  background: #f0f2f1;
  color: #6b7a78;
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 26px 12px;
}

.empty-state i {
  font-size: 34px;
  color: #c6d2cf;
  margin-bottom: 12px;
}

.empty-state-title {
  font-size: 14px;
  font-weight: 600;
  color: #3d5851;
  margin-bottom: 4px;
}

.empty-state .empty-text {
  margin: 0;
}

/* ═══════════════════════════════════════════════════════════
   Responsive
   ≥1201  3 columns (Performance spans 2)
   ≤1200  2 columns: Attendance, Payroll, Performance, Complains
          go full width; the rest pair up
   ≤900   stats 2 columns
   ≤768   everything stacks to 1 column
   ≤520 / ≤400  tighter spacing, compact rows
   ═══════════════════════════════════════════════════════════ */
@media (max-width: 1200px) {

  .panels-row,
  .panels-row-triple {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* 1 / -1 spans every column, so it also holds when the grid
     drops to a single column below. */
  .panel-attendance,
  .panel-payroll,
  .panel-performance,
  .panel-complains {
    grid-column: 1 / -1;
  }
}

@media (max-width: 900px) {
  .stats-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {

  .panels-row,
  .panels-row-triple {
    grid-template-columns: minmax(0, 1fr);
  }

  .dashboard {
    padding: 16px;
  }

  .header-card {
    padding: 20px;
  }

  .event-list,
  .leave-list {
    max-height: 320px;
  }
}

@media (max-width: 640px) {
  .header-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right,
  .checkin-time {
    align-items: flex-start;
  }

  .time-label {
    text-align: left;
  }
}

/* Performance row becomes a compact two-line grid on phones:
   name + badge on top, bar + percentage underneath. */
@media (max-width: 520px) {
  .dashboard {
    padding: 12px;
  }

  .stats-row,
  .panels-row,
  .panels-row-triple {
    gap: 12px;
  }

  .panels-row,
  .panels-row-triple {
    margin-bottom: 12px;
  }

  .stats-row,
  .header-card {
    margin-bottom: 12px;
  }

  .stat-card {
    padding: 16px 14px 12px;
  }

  .panel {
    padding: 18px 16px 14px;
  }

  .att-bars {
    gap: 4px;
  }

  .att-bar {
    width: 9px;
  }

  .att-legend {
    flex-wrap: wrap;
    gap: 8px 16px;
  }

  .leave-actions {
    flex-basis: 100%;
    margin-left: 44px;
  }

  .perf-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "emp badge"
      "bar pct";
    gap: 6px 10px;
  }

  .perf-emp,
  .sk-emp {
    grid-area: emp;
    width: auto;
  }

  .perf-badge,
  .perf-row>.sk-badge {
    grid-area: badge;
  }

  .perf-bar-track,
  .perf-row>.sk-bar-lg {
    grid-area: bar;
    min-width: 0;
  }

  .perf-pct,
  .perf-row>.sk-pct {
    grid-area: pct;
  }
}

@media (max-width: 400px) {
  .stats-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .att-tooltip {
    min-width: 116px;
  }
}

/* Bigger tap targets on touch screens. */
@media (pointer: coarse) {
  .leave-btn {
    padding: 8px 14px;
    font-size: 12px;
  }

  .primary-btn {
    padding: 12px 18px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Dashboard/Views/Admindashboard.vue